import { useCallback, useMemo, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import {
  AlertCircle,
  CheckCircle,
  FileText,
  Loader2,
  RefreshCcw,
  Upload,
  X,
} from 'lucide-react'
import {
  useCancelUpload,
  useUploadFile,
  useUploadHistory,
  useUploadStatus,
} from '@/hooks/useBox'
import { useResolvedWorkspace } from '@/hooks/useWorkspaces'
import type { FileUploadMetadata, UploadStatus } from '@/types/api'

interface PendingUploadItem {
  localId: string
  name: string
  size: number
  file?: File
  uploadId?: string
  error?: string
}

function formatSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function statusLabel(status?: UploadStatus) {
  switch (status) {
    case 'pending':
      return '待機中'
    case 'processing':
      return '処理中'
    case 'completed':
      return '完了'
    case 'failed':
      return '失敗'
    case 'cancelled':
      return 'キャンセル済み'
    default:
      return '登録中'
  }
}

function statusTone(status?: UploadStatus) {
  switch (status) {
    case 'completed':
      return 'text-green-600'
    case 'failed':
      return 'text-red-600'
    case 'cancelled':
      return 'text-amber-600'
    default:
      return 'text-muted-foreground'
  }
}

function UploadQueueRow({
  item,
  onRemove,
  onRetry,
}: {
  item: PendingUploadItem
  onRemove: (localId: string) => void
  onRetry: (localId: string) => void
}) {
  const cancelUpload = useCancelUpload()
  const statusQuery = useUploadStatus(item.uploadId ?? null, {
    enabled: !!item.uploadId,
  })

  const remoteStatus = statusQuery.data?.status
  const finalStatus = item.error ? 'failed' : remoteStatus
  const progress = statusQuery.data?.progress ?? (finalStatus === 'completed' ? 100 : 0)
  const canCancel = !!item.uploadId && finalStatus !== 'completed' && finalStatus !== 'failed' && finalStatus !== 'cancelled'

  async function handleRemove() {
    if (canCancel && item.uploadId) {
      await cancelUpload.mutateAsync(item.uploadId)
    }
    onRemove(item.localId)
  }

  return (
    <div className="flex flex-col gap-3 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-3">
        <FileText className="h-5 w-5 text-muted-foreground" />
        <div>
          <p className="font-medium">{item.name}</p>
          <p className="text-sm text-muted-foreground">{formatSize(item.size)}</p>
          {item.uploadId && (
            <p className="text-xs text-muted-foreground">Upload ID: {item.uploadId}</p>
          )}
          {item.error && (
            <p className="mt-1 text-xs text-red-600">{item.error}</p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-3">
        <div className="min-w-28 text-right">
          <div className={`text-sm font-medium ${statusTone(finalStatus)}`}>
            {statusQuery.isFetching && !finalStatus ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" /> 登録中...
              </span>
            ) : finalStatus === 'completed' ? (
              <span className="inline-flex items-center gap-2 text-green-600">
                <CheckCircle className="h-4 w-4" /> {statusLabel(finalStatus)}
              </span>
            ) : finalStatus === 'failed' ? (
              <span className="inline-flex items-center gap-2 text-red-600">
                <AlertCircle className="h-4 w-4" /> {statusLabel(finalStatus)}
              </span>
            ) : (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" /> {statusLabel(finalStatus)}
              </span>
            )}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">{progress}%</p>
        </div>
        <button
          onClick={() => onRetry(item.localId)}
          className="rounded-full p-1 hover:bg-muted disabled:opacity-50"
          aria-label={`${item.name} を再試行`}
          disabled={!item.error || !item.file}
        >
          <RefreshCcw className="h-4 w-4" />
        </button>
        <button
          onClick={() => void handleRemove()}
          className="rounded-full p-1 hover:bg-muted"
          aria-label={`${item.name} を削除`}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}

export function BoxPage() {
  const resolvedWorkspace = useResolvedWorkspace(import.meta.env.VITE_DEFAULT_WORKSPACE_ID)
  const workspaceId = resolvedWorkspace.workspaceId
  const workspaceName = resolvedWorkspace.workspace?.name ?? ''
  const uploadFile = useUploadFile()
  const historyQuery = useUploadHistory(workspaceId, 1, 10)
  const [files, setFiles] = useState<PendingUploadItem[]>([])
  const [tagsInput, setTagsInput] = useState('')
  const [category, setCategory] = useState('')
  const [visibility, setVisibility] = useState<'private' | 'team' | 'organization'>('private')

  const tags = useMemo(
    () => tagsInput.split(',').map((tag) => tag.trim()).filter((tag) => tag.length > 0),
    [tagsInput]
  )

  const submitFile = useCallback(async (file: File, localId: string) => {
    if (!workspaceId) return

    const metadata: FileUploadMetadata = {
      workspaceId,
      filename: file.name,
      size: file.size,
      mimeType: file.type || 'application/octet-stream',
      tags: tags.length > 0 ? tags : undefined,
      category: category.trim() || undefined,
      visibility,
    }

    setFiles((prev) =>
      prev.map((current) =>
        current.localId === localId
          ? { ...current, error: undefined, uploadId: undefined }
          : current
      )
    )

    try {
      const result = await uploadFile.mutateAsync({ file, metadata })
      setFiles((prev) =>
        prev.map((current) =>
          current.localId === localId
            ? { ...current, uploadId: result.uploadId, error: undefined }
            : current
        )
      )
    } catch (error) {
      setFiles((prev) =>
        prev.map((current) =>
          current.localId === localId
            ? {
                ...current,
                error: error instanceof Error ? error.message : 'アップロード登録に失敗しました',
              }
            : current
        )
      )
    }
  }, [category, tags, uploadFile, visibility, workspaceId])

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (!workspaceId) return

    for (const file of acceptedFiles) {
      const localId = crypto.randomUUID()
      setFiles((prev) => [
        {
          localId,
          name: file.name,
          size: file.size,
          file,
        },
        ...prev,
      ])

      await submitFile(file, localId)
    }
  }, [submitFile, workspaceId])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'text/markdown': ['.md'],
      'application/json': ['.json'],
      'text/csv': ['.csv'],
    },
    disabled: !workspaceId,
  })

  const removeFile = (localId: string) => {
    setFiles((prev) => prev.filter((file) => file.localId !== localId))
  }

  const retryFile = (localId: string) => {
    const entry = files.find((file) => file.localId === localId)
    if (!entry?.file) return
    void submitFile(entry.file, localId)
  }

  if (resolvedWorkspace.isLoading) {
    return <div className="p-6 text-sm text-muted-foreground">workspace を解決しています...</div>
  }

  if (resolvedWorkspace.error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-sm text-red-700">
        workspace の取得に失敗しました。認証状態と API 接続を確認してください。
      </div>
    )
  }

  if (!workspaceId) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-6 text-sm text-amber-900">
        利用可能な workspace がありません。先に Onboarding で workspace を作成してください。
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Context Bank BOX</h1>
        <p className="text-muted-foreground">
          実ファイルを投入して、workspace ごとの ingest 状況を追跡します。
        </p>
        <p className="mt-2 text-sm text-muted-foreground">
          対象 workspace: <span className="font-medium text-foreground">{workspaceName || workspaceId}</span>
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-[2fr,1fr]">
        <div className="space-y-6">
          <div
            {...getRootProps()}
            className={`cursor-pointer rounded-lg border-2 border-dashed p-12 text-center transition-colors ${
              isDragActive
                ? 'border-primary bg-primary/5'
                : 'border-border hover:border-primary/50'
            }`}
          >
            <input {...getInputProps()} />
            <Upload className="mx-auto h-12 w-12 text-muted-foreground" />
            <p className="mt-4 text-lg font-medium">
              {isDragActive
                ? 'ここにドロップ'
                : 'ファイルをドラッグ&ドロップ、またはクリックして選択'}
            </p>
            <p className="mt-2 text-sm text-muted-foreground">
              対応フォーマット: PDF, MD, JSON, CSV
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              選択したファイルはこの workspace に直接取り込まれます。
            </p>
          </div>

          {files.length > 0 && (
            <div className="rounded-lg border bg-card shadow-sm">
              <div className="border-b px-6 py-4">
                <h2 className="font-semibold">投入キュー</h2>
              </div>
              <div className="divide-y">
                {files.map((file) => (
                  <UploadQueueRow
                    key={file.localId}
                    item={file}
                    onRemove={removeFile}
                    onRetry={retryFile}
                  />
                ))}
              </div>
            </div>
          )}

          <div className="rounded-lg border bg-card shadow-sm">
            <div className="border-b px-6 py-4">
              <h2 className="font-semibold">最近の投入履歴</h2>
            </div>
            <div className="divide-y">
              {historyQuery.isLoading && (
                <div className="px-6 py-4 text-sm text-muted-foreground">履歴を読み込み中...</div>
              )}
              {!historyQuery.isLoading && (historyQuery.data?.items.length ?? 0) === 0 && (
                <div className="px-6 py-4 text-sm text-muted-foreground">履歴はまだありません。</div>
              )}
              {historyQuery.data?.items.map((item) => (
                <div key={item.uploadId} className="flex items-center justify-between px-6 py-4">
                  <div>
                    <p className="font-medium">{item.filename}</p>
                    <p className="text-sm text-muted-foreground">{formatSize(item.size)}</p>
                  </div>
                  <div className={`text-sm font-medium ${statusTone(item.status)}`}>
                    {statusLabel(item.status)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="rounded-lg border bg-card p-6 shadow-sm">
          <h2 className="mb-4 font-semibold">メタデータ</h2>
          <div className="space-y-4">
            <div>
              <label htmlFor="box-tags" className="text-sm font-medium">タグ</label>
              <input
                id="box-tags"
                value={tagsInput}
                onChange={(e) => setTagsInput(e.target.value)}
                placeholder="営業, 日報, 議事録"
                className="mt-1 block w-full rounded-md border bg-background px-3 py-2 text-sm"
              />
              <p className="mt-1 text-xs text-muted-foreground">カンマ区切りで入力します。</p>
            </div>
            <div>
              <label htmlFor="box-category" className="text-sm font-medium">カテゴリ</label>
              <input
                id="box-category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder="sales_activity"
                className="mt-1 block w-full rounded-md border bg-background px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label htmlFor="box-visibility" className="text-sm font-medium">共有範囲</label>
              <select
                id="box-visibility"
                value={visibility}
                onChange={(e) => setVisibility(e.target.value as 'private' | 'team' | 'organization')}
                className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm"
              >
                <option value="private">自分のみ</option>
                <option value="team">チーム</option>
                <option value="organization">組織全体</option>
              </select>
            </div>
            {uploadFile.isError && (
              <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                アップロード登録に失敗しました。入力内容と API 接続を確認してください。
              </div>
            )}
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">
              元ファイルは BOX 取り込み API に送られ、履歴と処理状態がこの画面に反映されます。
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
